
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { TraineeSupervisionRequests } from "@/components/trainee/TraineeSupervisionRequests";
import { SupervisionRequestsList } from "@/components/coach/SupervisionRequestsList";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCoachProfile } from "@/hooks/useCoachProfile";
import { ExternalChatButton } from "@/components/messaging/ExternalChatButton";
import { PermissionCard } from "@/components/supervision/PermissionCard";

const SupervisionManagement = () => {
  const [supervisionRequests, setSupervisionRequests] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { coachProfile, isCoach, checkCoachStatus } = useCoachProfile();
  const { t } = useTranslation('supervision');

  useEffect(() => {
    const initializeData = async () => {
      await checkAuth();
      const { isCoach: coachStatus, coachProfile: profile } = await checkCoachStatus();
      setLoading(false);
      
      // Récupérer les données après avoir vérifié le statut coach
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      if (coachStatus && profile) {
        await fetchCoachData(profile.id);
      } else {
        await fetchTraineeData(user.id);
      }
    };
    initializeData();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
    }
  };

  const fetchData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log("[FETCH DATA] Récupération des données pour l'utilisateur:", user.id);
      console.log("[FETCH DATA] isCoach:", isCoach, "coachProfile:", coachProfile);

      if (isCoach && coachProfile) {
        console.log("[FETCH DATA] Mode Coach - récupération données coach");
        await fetchCoachData(coachProfile.id);
      } else {
        console.log("[FETCH DATA] Mode Stagiaire - récupération données stagiaire");
        await fetchTraineeData(user.id);
      }
    } catch (error: any) {
      console.error("Erreur complète fetchData:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('management.errors.loadData'),
      });
    }
  };

  const fetchCoachData = async (coachId: string) => {
    // Récupérer les demandes envoyées par le coach
    const { data: requests, error: requestsError } = await supabase
      .from("supervision_requests")
      .select("*")
      .eq("coach_id", coachId)
      .order("requested_at", { ascending: false });

    if (requestsError) {
      console.error("Erreur demandes coach:", requestsError);
      throw requestsError;
    }

    console.log("Demandes coach brutes:", requests);

    // Enrichir avec les informations des stagiaires
    const requestsWithTrainees = await Promise.all(
      (requests || []).map(async (request) => {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("username, avatar_url, email, phone_number, whatsapp_enabled, telegram_username, telegram_enabled, preferred_messaging_platform")
          .eq("id", request.trainee_user_id)
          .maybeSingle();

        if (profileError) {
          console.error("Erreur profil stagiaire:", profileError);
        }

        return {
          ...request,
          profiles: profile || { username: t('coach.unknownTrainee') }
        };
      })
    );

    console.log("Demandes avec stagiaires:", requestsWithTrainees);

    // Récupérer les permissions accordées au coach
    const { data: perms, error: permsError } = await supabase
      .from("data_sharing_permissions")
      .select("*")
      .eq("coach_id", coachId)
      .eq("is_active", true);

    if (permsError) {
      console.error("Erreur permissions coach:", permsError);
      throw permsError;
    }

    console.log("Permissions coach brutes:", perms);

    // Enrichir avec les informations des stagiaires
    const permsWithTrainees = await Promise.all(
      (perms || []).map(async (permission) => {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("username, avatar_url, email, phone_number, whatsapp_enabled, telegram_username, telegram_enabled, preferred_messaging_platform")
          .eq("id", permission.trainee_user_id)
          .maybeSingle();

        if (profileError) {
          console.error("Erreur profil stagiaire pour permission:", profileError);
        }

        return {
          ...permission,
          profiles: profile || { username: t('coach.unknownTrainee') }
        };
      })
    );

    console.log("Permissions avec stagiaires:", permsWithTrainees);

    // Regrouper les permissions par stagiaire
    const groupedPermissions = permsWithTrainees.reduce((acc, permission) => {
      const traineeId = permission.trainee_user_id;
      const existingPermission = acc.find(p => p.trainee_user_id === traineeId);
      
      if (existingPermission) {
        // Ajouter le type de permission à la liste
        existingPermission.permission_types = [
          ...(existingPermission.permission_types || [existingPermission.permission_type]),
          permission.permission_type
        ];
        // Garder l'ID de la première permission
        if (!existingPermission.all_permission_ids) {
          existingPermission.all_permission_ids = [existingPermission.id];
        }
        existingPermission.all_permission_ids.push(permission.id);
      } else {
        acc.push({
          ...permission,
          permission_types: [permission.permission_type],
          all_permission_ids: [permission.id]
        });
      }
      
      return acc;
    }, [] as any[]);

    console.log("Permissions regroupées:", groupedPermissions);

    setSupervisionRequests(requestsWithTrainees);
    setPermissions(groupedPermissions);
  };

  const fetchTraineeData = async (userId: string) => {
    console.log("[TRAINEE] Début fetchTraineeData pour userId:", userId);
    
    // Récupérer les demandes reçues
    const { data: requests, error: requestsError } = await supabase
      .from("supervision_requests")
      .select("*")
      .eq("trainee_user_id", userId)
      .order("requested_at", { ascending: false });

    if (requestsError) {
      console.error("[TRAINEE] Erreur demandes:", requestsError);
      throw requestsError;
    }

    console.log("[TRAINEE] Demandes brutes:", requests);

    // Enrichir avec les informations des coaches
    const requestsWithCoaches = await Promise.all(
      (requests || []).map(async (request) => {
        const { data: coach, error: coachError } = await supabase
          .from("coaches")
          .select("coach_name, contact_email")
          .eq("id", request.coach_id)
          .maybeSingle();

        if (coachError) {
          console.error("[TRAINEE] Erreur coach:", coachError);
        }

        return {
          ...request,
          coaches: coach || { coach_name: t('coach.unknownCoach'), contact_email: "" }
        };
      })
    );

    console.log("[TRAINEE] Demandes avec coaches:", requestsWithCoaches);

    // Récupérer les permissions accordées
    const { data: perms, error: permsError } = await supabase
      .from("data_sharing_permissions")
      .select("*")
      .eq("trainee_user_id", userId)
      .eq("is_active", true);

    if (permsError) {
      console.error("[TRAINEE] Erreur permissions:", permsError);
      throw permsError;
    }

    console.log("[TRAINEE] Permissions brutes:", perms);
    console.log("[TRAINEE] Nombre de permissions:", perms?.length || 0);

    // Enrichir avec les informations des coaches via la vue sécurisée
    const permsWithCoaches = await Promise.all(
      (perms || []).map(async (permission) => {
        const { data: coachProfile, error: coachError } = await supabase
          .from("coaches")
          .select("coach_name, user_id")
          .eq("id", permission.coach_id)
          .maybeSingle();

        if (coachError) {
          console.error("Erreur coach pour permission:", coachError);
        }

        // Récupérer les infos de contact depuis profiles
        let contactInfo = null;
        if (coachProfile?.user_id) {
          const { data: contact } = await supabase
            .from("profiles")
            .select("phone_number, whatsapp_enabled, telegram_username, telegram_enabled, preferred_messaging_platform")
            .eq("id", coachProfile.user_id)
            .maybeSingle();
          contactInfo = contact;
        }

        return {
          ...permission,
          coach_id: coachProfile?.user_id || permission.coach_id,
          coaches: {
            coach_name: coachProfile?.coach_name || t('coach.unknownCoach'),
            ...contactInfo
          }
        };
      })
    );

    console.log("[TRAINEE] Permissions avec coaches:", permsWithCoaches);
    console.log("[TRAINEE] Nombre permissions avec coaches:", permsWithCoaches?.length || 0);

    // Regrouper les permissions par coach
    const groupedPermissions = permsWithCoaches.reduce((acc, permission) => {
      const coachId = permission.coach_id;
      console.log("[TRAINEE] Traitement permission pour coach_id:", coachId, "permission:", permission);
      
      const existingPermission = acc.find(p => p.coach_id === coachId);
      
      if (existingPermission) {
        console.log("[TRAINEE] Permission existante trouvée, ajout du type");
        // Ajouter le type de permission à la liste
        existingPermission.permission_types = [
          ...(existingPermission.permission_types || [existingPermission.permission_type]),
          permission.permission_type
        ];
        // Garder l'ID de la première permission pour la révocation
        if (!existingPermission.all_permission_ids) {
          existingPermission.all_permission_ids = [existingPermission.id];
        }
        existingPermission.all_permission_ids.push(permission.id);
      } else {
        console.log("[TRAINEE] Nouvelle permission, ajout au tableau");
        acc.push({
          ...permission,
          permission_types: [permission.permission_type],
          all_permission_ids: [permission.id]
        });
      }
      
      return acc;
    }, [] as any[]);

    console.log("[TRAINEE] Permissions regroupées:", groupedPermissions);
    console.log("[TRAINEE] Nombre permissions regroupées:", groupedPermissions?.length || 0);

    setSupervisionRequests(requestsWithCoaches);
    setPermissions(groupedPermissions);
    
    console.log("[TRAINEE] State mis à jour - requests:", requestsWithCoaches.length, "permissions:", groupedPermissions.length);
  };

  const revokePermission = async (permissionId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Récupérer la permission pour avoir les IDs à supprimer
      const permissionToRevoke = permissions.find(p => p.id === permissionId);
      if (!permissionToRevoke) return;

      // Supprimer toutes les permissions associées
      const idsToDelete = permissionToRevoke.all_permission_ids || [permissionId];
      
      const { data: firstPermission, error: permError } = await supabase
        .from("data_sharing_permissions")
        .select("coach_id, trainee_user_id")
        .eq("id", idsToDelete[0])
        .single();

      if (permError) throw permError;

      // Supprimer toutes les permissions
      const { error: permissionError } = await supabase
        .from("data_sharing_permissions")
        .delete()
        .in("id", idsToDelete);

      if (permissionError) throw permissionError;

      // Mettre à jour le statut de la demande de supervision correspondante
      const { error: requestError } = await supabase
        .from("supervision_requests")
        .update({ 
          status: 'revoked',
          responded_at: new Date().toISOString()
        })
        .eq("coach_id", firstPermission.coach_id)
        .eq("trainee_user_id", firstPermission.trainee_user_id)
        .eq("status", "accepted");

      if (requestError) {
        console.error("Erreur lors de la mise à jour de la demande:", requestError);
      }

      toast({
        title: t('management.success.permissionRevoked'),
        description: t('management.success.permissionRevoked'),
      });

      fetchData();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
        <NavigationMenu />
        <div className="container mx-auto p-8">
          <div className="flex justify-center items-center h-64">
            {t('management.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto p-4 md:p-8">
        <h1 className="text-3xl font-bold text-golf-800 mb-8">{t('management.title')}</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {isCoach ? (
            <>
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('management.coach.requests.title')}</h2>
                <SupervisionRequestsList 
                  requests={supervisionRequests} 
                  onUpdate={fetchData}
                />
              </div>

              {/* Vue Coach: Permissions accordées par les stagiaires */}
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('management.coach.permissions.title')}</h2>
                {permissions.length === 0 ? (
                  <Card>
                    <CardContent className="p-6 text-center text-gray-500">
                      {t('management.coach.permissions.noPermissions')}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {permissions.map((permission) => (
                      <PermissionCard
                        key={permission.id}
                        permission={permission}
                        isCoach={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Vue Stagiaire: Demandes reçues */}
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('management.trainee.requests.title')}</h2>
                <TraineeSupervisionRequests 
                  requests={supervisionRequests} 
                  onUpdate={fetchData}
                />
              </div>

              {/* Vue Stagiaire: Permissions accordées */}
              <div>
                <h2 className="text-xl font-semibold mb-4">{t('management.trainee.permissions.title')}</h2>
                {permissions.length === 0 ? (
                  <Card>
                    <CardContent className="p-6 text-center text-gray-500">
                      {t('management.trainee.permissions.noPermissions')}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {permissions.map((permission) => (
                      <PermissionCard
                        key={permission.id}
                        permission={permission}
                        isCoach={false}
                        onRevoke={revokePermission}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupervisionManagement;
